package org.vreo.devicehub.features.device.presentation.deviceManagement.mainDeviceNavigation

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PrimaryTabRow
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.koin.compose.viewmodel.koinViewModel
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.StringResource

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.device_management_details_tab_label
import devicehub.composeapp.generated.resources.device_management_messages_tab_label
import devicehub.composeapp.generated.resources.device_management_output_tab_label
import devicehub.composeapp.generated.resources.device_management_roi_tab_label
import devicehub.composeapp.generated.resources.device_management_settings_tab_label
import devicehub.composeapp.generated.resources.vreo_logo
import org.jetbrains.compose.resources.painterResource


/**
 * Device management destinations
 *
 * @property label destination/tab label
 */
private enum class DeviceManagementDestinations(
    val label: StringResource,
) {
    DETAILS(Res.string.device_management_details_tab_label),
    MESSAGES(Res.string.device_management_messages_tab_label),
    SETTINGS(Res.string.device_management_settings_tab_label),
    ROI(Res.string.device_management_roi_tab_label),
    VRM_OUTPUT(Res.string.device_management_output_tab_label),
}

/**
 * Device management screen root
 *
 * @param viewModel view model
 * @param onBackNavigation callback to be called on back navigation
 * @param detailsView details tab content
 * @param messagesView messages tab content
 * @param settingsView settings tab content
 * @param outputView output tab content
 */
@Composable
fun DeviceManagementScreenRoot(
    viewModel: DeviceManagementViewModel = koinViewModel(),
    onBackNavigation: () -> Unit,
    detailsView: @Composable (() -> Unit),
    messagesView: @Composable (() -> Unit),
    roiView: @Composable (() -> Unit),
    settingsView: @Composable (() -> Unit),
    outputView: @Composable (() -> Unit)
) {
    val state: DeviceManagementState by viewModel.state.collectAsStateWithLifecycle()

    DeviceManagementView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is DeviceManagementActions.OnBackNavigation -> {
                    onBackNavigation()
                    action
                }

                else -> action
            }
            viewModel.onAction(actionRequest)
        },
        detailsView = detailsView,
        messagesView = messagesView,
        roiView = roiView,
        settingsView = settingsView,
        outputView = outputView
    )
}

/**
 * Device management view, provides top leve view with tabs for sub menu navigation for the
 * selected device
 *
 * @param state state to present
 * @param onAction action handler
 * @param detailsView details tab content
 * @param messagesView messages tab content
 * @param settingsView settings tab content
 * @param outputView output tab content
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DeviceManagementView(
    state: DeviceManagementState,
    onAction: (DeviceManagementActions) -> Unit = {},
    detailsView: @Composable (() -> Unit),
    messagesView: @Composable (() -> Unit),
    roiView: @Composable (() -> Unit),
    settingsView: @Composable (() -> Unit),
    outputView: @Composable (() -> Unit)
) {
    Scaffold(
        topBar = {
            CenterAlignedTopAppBar(
                title = {},
                navigationIcon = {
                    IconButton(
                        onClick = {
                            onAction(DeviceManagementActions.OnBackNavigation)
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = null
                        )
                    }
                }
            )
        },
        bottomBar = {
            Box(
                contentAlignment = Alignment.CenterStart,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(58.dp)
                    .padding(horizontal = 4.dp, vertical = 4.dp)
            ) {
                Image(
                    painter = painterResource(Res.drawable.vreo_logo),
                    contentDescription = null,
                    contentScale = ContentScale.FillHeight
                )
            }
        }
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .widthIn(900.dp)
                .padding(innerPadding)
        ) {
            PrimaryTabRow(
                selectedTabIndex = state.selectedTabIndex,
            ) {
                DeviceManagementDestinations.entries.forEachIndexed { index, destination ->
                    Tab(
                        selected = index == state.selectedTabIndex,
                        onClick = {
                            onAction(DeviceManagementActions.OnUpdateTabIndex(index))
                        },
                        text = {
                            Text(
                                text = stringResource(destination.label),
                                maxLines = 2,
                                overflow = TextOverflow.Ellipsis,
                                style = MaterialTheme.typography.bodyMedium.copy(
                                    fontWeight = FontWeight.Bold
                                )
                            )
                        },
                        selectedContentColor = MaterialTheme.colorScheme.primary,
                        unselectedContentColor = Color.Black.copy(alpha = 0.7f)
                    )
                }
            }
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
            ) {
                when (state.selectedTabIndex) {
                    DeviceManagementDestinations.DETAILS.ordinal -> {
                        detailsView()
                    }

                    DeviceManagementDestinations.MESSAGES.ordinal -> {
                        messagesView()
                    }

                    DeviceManagementDestinations.SETTINGS.ordinal -> {
                        settingsView()
                    }

                    DeviceManagementDestinations.ROI.ordinal -> {
                        roiView()
                    }

                    DeviceManagementDestinations.VRM_OUTPUT.ordinal -> {
                        outputView()
                    }
                }
            }
        }
    }
}

@Preview
@Composable
fun DeviceManagementScreenPreview() {
    MaterialTheme {
        DeviceManagementView(
            state = DeviceManagementState(),
            detailsView = {},
            messagesView = {},
            settingsView = {},
            roiView = {},
            outputView = {}
        )
    }
}



